'use client';

import { Add, Delete } from "@mui/icons-material";
import { Box, Button, FormHelperText, IconButton, InputBase, Stack } from "@mui/material";
import { useEffect, useRef } from "react";

export type EmailEditorProps = {
    value?: string | null;
    onChange: (value: string | undefined) => void;
    error?: string | null;
}

export default function EmailEditor({ value, onChange, error }: EmailEditorProps) {
    const hasValue = value !== null && value !== undefined;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const prevHasValueRef = useRef(hasValue);

    useEffect(() => {
        if (!prevHasValueRef.current && hasValue) {
            inputRef.current?.focus();
        }
        prevHasValueRef.current = hasValue;
    }, [hasValue]);

    if (!hasValue) {
        return (
            <Button
                variant="outlined"
                color="breakout"
                startIcon={<Add />}
                onClick={() => onChange('')}
                fullWidth
            >
                E-Mail hinzufügen
            </Button>
        );
    }

    return (
        <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'flex-start' }}>
            <Box sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid",
                        borderColor: error ? "error.main" : "var(--breakout)",
                        borderRadius: 1,
                        minHeight: 40,
                        px: 1.5,
                        "&:focus-within": {
                            borderColor: error ? "error.main" : "primary.main",
                            boxShadow: (theme) =>
                                error ? `0 0 0 1px ${theme.palette.error.main}` : `0 0 0 1px ${theme.palette.primary.main}`,
                        },
                    }}
                >
                    <InputBase
                        inputRef={inputRef}
                        value={value ?? ""}
                        onChange={(event) => onChange(event.target.value)}
                        placeholder="E-Mail"
                        inputProps={{ "aria-label": "E-Mail" }}
                        sx={{ flex: "1 1 auto" }}
                    />
                </Box>
                {error ? (
                    <FormHelperText error sx={{ mx: 1.5 }}>
                        {error}
                    </FormHelperText>
                ) : null}
            </Box>
            <IconButton
                aria-label="E-Mail entfernen"
                color="error"
                onClick={() => onChange(undefined)}
                sx={{ width: 40, height: 40, alignSelf: 'flex-start' }}
            >
                <Delete sx={{ fontSize: 28 }} />
            </IconButton>
        </Stack>
    );
}
